import type {
  InadimplenteAgrupado,
  InadimplenteRow,
  PendenteAgrupado,
  PendingSale,
} from './tipos';

/**
 * De CONTRATOS para PESSOAS.
 *
 * As listas do dashboard mostram cinco linhas. Cruas, um cliente com cinco
 * contratos em atraso ocupava as cinco repetindo o próprio nome e o mesmo
 * valor: o card inteiro para dizer uma coisa só, com o resto da carteira
 * invisível atrás dele.
 *
 * O agrupamento acontece sobre a lista INTEIRA, antes do corte no top 5. Fazer
 * depois não recuperaria nada — as outras pessoas já teriam ficado de fora.
 *
 * Funções PURAS, fora do componente, porque é a lógica com mais chance de errar
 * em silêncio: um total somado errado aqui aparece na tela com a mesma cara de
 * um total certo.
 */

/**
 * A chave é o nome normalizado.
 *
 * Não temos CPF do consorciado nestas views, e o nome é o que a tela mostra: se
 * duas linhas se leem como a mesma pessoa, tratá-las como uma é o que quem olha
 * espera. O risco inverso (dois homônimos somados) existe, mas o efeito é uma
 * linha a mais no total de uma delas — não a perda de ninguém da lista.
 */
const chaveDoNome = (nome: string | null | undefined) => (nome || '').trim().toUpperCase();

export const agruparInadimplentes = (linhas: InadimplenteRow[]): InadimplenteAgrupado[] => {
  const porPessoa = new Map<string, InadimplenteAgrupado>();
  for (const linha of linhas) {
    const chave = chaveDoNome(linha.consorciado);
    const atual = porPessoa.get(chave);
    if (atual) {
      atual.valor_atraso += Number(linha.valor_atraso || 0);
      // O PIOR atraso da pessoa, não o último lido: é ele que decide urgência.
      atual.parcelas_em_aberto = Math.max(
        atual.parcelas_em_aberto,
        linha.parcelas_em_aberto ?? 0,
      );
      atual.contratos += 1;
    } else {
      porPessoa.set(chave, {
        id: linha.id,
        consorciado: linha.consorciado,
        vendedor_nome: linha.vendedor_nome,
        parcelas_em_aberto: linha.parcelas_em_aberto ?? 0,
        valor_atraso: Number(linha.valor_atraso || 0),
        contratos: 1,
      });
    }
  }
  // Reordena: somada, uma pessoa com vários contratos pode passar à frente de
  // quem liderava sozinho — e passar é o certo, porque é mais dinheiro parado.
  return [...porPessoa.values()].sort((a, b) => b.valor_atraso - a.valor_atraso);
};

export const agruparPendentes = (linhas: PendingSale[]): PendenteAgrupado[] => {
  const porPessoa = new Map<string, PendenteAgrupado>();
  for (const linha of linhas) {
    const chave = chaveDoNome(linha.nome_consorciado);
    const atual = porPessoa.get(chave);
    if (atual) {
      atual.valor_parcela += Number(linha.valor_parcela || 0);
      atual.cotas += 1;
      // O grupo só continua no rótulo enquanto TODAS as cotas forem dele.
      // Mostrar "Grupo 3308" para quem tem cotas em dois grupos seria afirmar
      // algo falso sobre metade delas.
      if (atual.group_number !== linha.group_number) atual.group_number = null;
    } else {
      porPessoa.set(chave, {
        id: linha.id,
        nome_consorciado: linha.nome_consorciado,
        group_number: linha.group_number,
        contrato: linha.contrato,
        valor_parcela: Number(linha.valor_parcela || 0),
        cotas: 1,
      });
    }
  }
  // Mantém a ordem de chegada — a consulta já vem por created_at desc. Aqui o
  // que interessa é o que entrou por último, não o maior valor.
  return [...porPessoa.values()];
};
